import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, MapPin, Truck, User, Clock, Package, AlertTriangle, FileCheck, QrCode, Weight, Ruler, Download } from "lucide-react";
import api, { downloadPdf } from "@/lib/api";
import { useFetch, Spinner } from "@/components/portal/ui";
import { StatusBadge } from "@/components/StatusBadge";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";

function fmt(t) { return t ? new Date(t).toLocaleString("en-ZA", { dateStyle: "medium", timeStyle: "short" }) : "—"; }

export default function ShipmentDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { data, loading } = useFetch(() => api.get(`/portal/shipments/${id}`).then((r) => r.data), [id]);
  if (loading) return <Spinner />;
  if (!data) return null;
  const pct = data.status === "Delivered" ? 100 : Math.round((1 - (data.distance_remaining_km || 0) / (data.distance_km || 1)) * 100);

  return (
    <div>
      <Button variant="ghost" size="sm" onClick={() => nav(-1)} className="mb-4" data-testid="back-btn"><ArrowLeft size={16} className="mr-1" /> Back</Button>
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-heading font-extrabold font-mono">{data.tracking_number}</h1>
          <p className="text-muted-foreground mt-1">{data.service_type} · {data.cargo_type}</p>
        </div>
        <div className="flex items-center gap-3">
          <StatusBadge status={data.status} />
          <div className="w-14 h-14 rounded-lg bg-[#0B0F17] text-white flex items-center justify-center"><QrCode size={36} /></div>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card p-6">
        <div className="flex justify-between text-sm mb-2">
          <span className="font-semibold">{data.origin}</span>
          <span className="text-muted-foreground">{data.distance_remaining_km} km remaining of {data.distance_km} km</span>
          <span className="font-semibold">{data.destination}</span>
        </div>
        <Progress value={pct} className="h-2" />
        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { icon: MapPin, l: "Current Location", v: data.current_location },
            { icon: Clock, l: "ETA", v: fmt(data.eta) },
            { icon: Truck, l: "Vehicle", v: data.vehicle?.reg_no || "Assigning" },
            { icon: User, l: "Driver", v: data.driver?.name || "Assigning" },
            { icon: Weight, l: "Weight", v: `${data.weight_kg} kg` },
            { icon: Ruler, l: "Dimensions", v: data.dimensions || "—" },
            { icon: Package, l: "Priority", v: data.priority },
            { icon: Clock, l: "Pickup", v: fmt(data.pickup_time) },
          ].map((x) => (
            <div key={x.l} className="rounded-lg bg-muted/50 p-3">
              <x.icon size={16} className="text-primary" />
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground mt-2">{x.l}</p>
              <p className="text-sm font-semibold">{x.v}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-6 grid lg:grid-cols-2 gap-6">
        <div className="rounded-xl border border-border bg-card p-6">
          <h3 className="font-heading font-bold flex items-center gap-2 mb-4"><Package size={18} className="text-primary" /> Route history</h3>
          <div className="space-y-4">
            {[...(data.route_history || [])].reverse().map((r, i) => (
              <div key={i} className="flex gap-4">
                <div className="flex flex-col items-center">
                  <div className={`w-3 h-3 rounded-full ${i === 0 ? "bg-primary" : "bg-border"}`} />
                  {i < data.route_history.length - 1 && <div className="w-px flex-1 bg-border" />}
                </div>
                <div className="pb-2">
                  <p className="font-semibold text-sm">{r.status}</p>
                  <p className="text-sm text-muted-foreground">{r.location} · {r.note}</p>
                  <p className="text-xs text-muted-foreground font-mono mt-0.5">{fmt(r.time)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-6">
          {data.exceptions?.length > 0 && (
            <div className="rounded-xl border border-orange-200 bg-orange-50 dark:bg-orange-500/10 p-5">
              <h3 className="font-heading font-bold flex items-center gap-2 text-orange-700"><AlertTriangle size={18} /> Exceptions</h3>
              {data.exceptions.map((e, i) => <p key={i} className="mt-2 text-sm text-orange-700">{e.type}: {e.note}</p>)}
            </div>
          )}
          {data.pod ? (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 dark:bg-emerald-500/10 p-5">
              <h3 className="font-heading font-bold flex items-center gap-2 text-emerald-700"><FileCheck size={18} /> Proof of Delivery</h3>
              <div className="mt-3 space-y-1 text-sm">
                <p><span className="text-muted-foreground">Recipient:</span> <b>{data.pod.recipient}</b></p>
                <p><span className="text-muted-foreground">Signed:</span> <b>{fmt(data.pod.signed_at)}</b></p>
                <p><span className="text-muted-foreground">Quantity:</span> <b>{data.pod.quantity_delivered}</b></p>
                <p><span className="text-muted-foreground">Notes:</span> {data.pod.notes}</p>
              </div>
              {data.pod.photos?.length > 0 && (
                <div className="mt-3" data-testid="pod-gallery">
                  <p className="text-[11px] uppercase tracking-wide text-emerald-700 font-semibold mb-1.5">Delivery photos</p>
                  <div className="flex flex-wrap gap-2">
                    {data.pod.photos.map((p, i) => {
                      const src = `${process.env.REACT_APP_BACKEND_URL}/api/files/${p}?auth=${localStorage.getItem("ebik_token")}`;
                      return (
                        <a key={i} href={src} target="_blank" rel="noreferrer" data-testid={`pod-photo-${i}`}>
                          <img src={src} alt={`Delivery proof ${i + 1}`}
                            className="w-20 h-20 object-cover rounded-md border border-emerald-300 hover:scale-105 transition-transform" />
                        </a>
                      );
                    })}
                  </div>
                </div>
              )}
              <Button size="sm" variant="outline" className="mt-3 border-emerald-300 text-emerald-700 hover:bg-emerald-100" data-testid="pod-download" onClick={async () => { try { await downloadPdf(`/portal/shipments/${data.id}/pod/pdf`, `POD-${data.tracking_number}.pdf`); toast.success("POD downloaded"); } catch { toast.error("Download failed"); } }}><Download size={14} className="mr-1.5" /> Download POD (PDF)</Button>
            </div>
          ) : (
            <div className="rounded-xl border border-border bg-card p-5 text-sm text-muted-foreground">Proof of delivery will appear here once the shipment is delivered.</div>
          )}
        </div>
      </div>
    </div>
  );
}
